import { common, crypto, decryptNEP2, encryptNEP2, privateKeyToPublicKey, publicKeyToAddress, } from '@neo-one/client-common';
import { utils } from '@neo-one/utils';
import debug from 'debug';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import * as args from '../../args';
import { LockedAccountError, UnknownAccountError } from '../../errors';
const logger = debug('NEOONE:LocalKeystore');
const flattenWallets = (wallets) => _.flatten(Object.values(wallets)
    .filter(utils.notNull)
    .map((networkWallets) => Object.values(networkWallets))).filter(utils.notNull);
export class LocalKeyStore {
    constructor(store) {
        this.walletsInternal$ = new BehaviorSubject({});
        this.wallets$ = this.walletsInternal$.pipe(distinctUntilChanged((a, b) => _.isEqual(a, b)), map(flattenWallets));
        this.accountsInternal$ = new BehaviorSubject([]);
        this.wallets$
            .pipe(map((wallets) => wallets.map(({ userAccount }) => userAccount)))
            .subscribe(this.accountsInternal$);
        this.userAccounts$ = this.accountsInternal$;
        this.currentAccountInternal$ = new BehaviorSubject(undefined);
        this.currentUserAccount$ = this.currentAccountInternal$.pipe(distinctUntilChanged());
        this.store = store;
        if (store.getWalletsSync !== undefined) {
            this.initWithWallets(store.getWalletsSync());
        }
        this.initPromise = this.init();
    }
    getCurrentUserAccount() {
        return this.currentAccountInternal$.getValue();
    }
    getUserAccounts() {
        return this.accountsInternal$.getValue();
    }
    get wallets() {
        return flattenWallets(this.walletsObj);
    }
    async sign({ account, message, }) {
        await this.initPromise;
        return this.capture(async () => {
            const privateKey = this.getPrivateKey(account);
            return crypto
                .sign({ message: Buffer.from(message, 'hex'), privateKey: common.stringToPrivateKey(privateKey) })
                .toString('hex');
        }, 'neo_sign');
    }
    async selectUserAccount(id) {
        if (id === undefined) {
            this.currentAccountInternal$.next(undefined);
            this.newCurrentAccount();
        }
        else {
            const { userAccount } = this.getWallet(id);
            this.currentAccountInternal$.next(userAccount);
        }
    }
    async updateUserAccountName({ id, name }) {
        return this.capture(async () => {
            await this.initPromise;
            const wallet = this.getWallet(id);
            let newWallet;
            const userAccount = {
                id: wallet.userAccount.id,
                name,
                publicKey: wallet.userAccount.publicKey,
            };
            if (wallet.type === 'locked') {
                newWallet = {
                    type: 'locked',
                    userAccount,
                    nep2: wallet.nep2,
                };
            }
            else {
                newWallet = {
                    type: 'unlocked',
                    userAccount,
                    privateKey: wallet.privateKey,
                    nep2: wallet.nep2,
                };
            }
            await this.capture(async () => this.store.saveWallet(newWallet), 'neo_save_wallet');
            this.updateWallet(newWallet);
        }, 'neo_update_account_name');
    }
    getWallet({ address, network }) {
        const wallets = this.walletsObj[network];
        if (wallets === undefined) {
            throw new UnknownAccountError(address);
        }
        const wallet = wallets[address];
        if (wallet === undefined) {
            throw new UnknownAccountError(address);
        }
        return wallet;
    }
    getWallet$({ address, network }) {
        return this.walletsInternal$.pipe(map((wallets) => {
            const networkWallets = wallets[network];
            if (networkWallets === undefined) {
                return undefined;
            }
            return networkWallets[address];
        }));
    }
    async addUserAccount({ network, privateKey: privateKeyIn, name, password, nep2: nep2In, }) {
        await this.initPromise;
        return this.capture(async () => {
            let pk = privateKeyIn;
            let nep2 = nep2In;
            if (pk === undefined) {
                if (nep2 === undefined || password === undefined) {
                    throw new Error('Expected private key or password and NEP-2 key');
                }
                pk = await decryptNEP2({ encryptedKey: nep2, password });
            }
            const privateKey = args.assertPrivateKey('privateKey', pk);
            const publicKey = privateKeyToPublicKey(privateKey);
            const address = publicKeyToAddress(publicKey);
            if (nep2 === undefined && password !== undefined) {
                nep2 = await encryptNEP2({ privateKey, password });
            }
            const userAccount = {
                id: {
                    network,
                    address,
                },
                name: name === undefined ? address : name,
                publicKey,
            };
            const unlockedWallet = {
                type: 'unlocked',
                userAccount,
                nep2,
                privateKey,
            };
            let wallet = unlockedWallet;
            if (nep2 !== undefined) {
                wallet = { type: 'locked', userAccount, nep2 };
            }
            await this.capture(async () => this.store.saveWallet(wallet), 'neo_save_wallet');
            this.updateWallet(unlockedWallet);
            if (this.currentAccount === undefined) {
                this.currentAccountInternal$.next(wallet.userAccount);
            }
            return unlockedWallet;
        }, 'neo_add_account');
    }
    async deleteUserAccount(id) {
        await this.initPromise;
        return this.capture(async () => {
            const { walletsObj: wallets } = this;
            const networkWalletsIn = wallets[id.network];
            if (networkWalletsIn === undefined) {
                return;
            }
            const { [id.address]: wallet, ...networkWallets } = networkWalletsIn;
            if (wallet === undefined) {
                return;
            }
            await this.capture(async () => this.store.deleteWallet(wallet), 'neo_delete_wallet');
            this.walletsInternal$.next({
                ...wallets,
                [id.network]: networkWallets,
            });
            if (this.currentAccount !== undefined &&
                this.currentAccount.id.network === id.network &&
                this.currentAccount.id.address === id.address) {
                this.newCurrentAccount();
            }
        }, 'neo_delete_account');
    }
    async unlockWallet({ id, password, }) {
        await this.initPromise;
        return this.capture(async () => {
            const wallet = this.getWallet(id);
            if (wallet.type === 'unlocked') {
                return;
            }
            const privateKey = await decryptNEP2({
                encryptedKey: wallet.nep2,
                password,
            });
            this.updateWallet({
                type: 'unlocked',
                userAccount: wallet.userAccount,
                privateKey,
                nep2: wallet.nep2,
            });
        }, 'neo_unlock_wallet');
    }
    async lockWallet(id) {
        await this.initPromise;
        const wallet = this.getWallet(id);
        if (wallet.type === 'locked' || wallet.nep2 === undefined) {
            return;
        }
        this.updateWallet({
            type: 'locked',
            userAccount: wallet.userAccount,
            nep2: wallet.nep2,
        });
    }
    async init() {
        const walletsList = await this.store.getWallets();
        this.initWithWallets(walletsList);
    }
    initWithWallets(walletsList) {
        const wallets = walletsList.reduce((acc, wallet) => ({
            ...acc,
            [wallet.userAccount.id.network]: {
                ...(acc[wallet.userAccount.id.network] === undefined ? {} : acc[wallet.userAccount.id.network]),
                [wallet.userAccount.id.address]: wallet,
            },
        }), {});
        this.walletsInternal$.next(wallets);
        this.newCurrentAccount();
    }
    getPrivateKey(id) {
        const wallet = this.getWallet({
            network: id.network,
            address: id.address,
        });
        if (wallet.type === 'locked') {
            throw new LockedAccountError(id.address);
        }
        return wallet.privateKey;
    }
    updateWallet(wallet) {
        const { walletsObj: wallets } = this;
        this.walletsInternal$.next({
            ...wallets,
            [wallet.userAccount.id.network]: {
                ...(wallets[wallet.userAccount.id.network] === undefined ? {} : wallets[wallet.userAccount.id.network]),
                [wallet.userAccount.id.address]: wallet,
            },
        });
    }
    newCurrentAccount() {
        const allAccounts = this.wallets.map(({ userAccount: value }) => value);
        const account = allAccounts[0];
        this.currentAccountInternal$.next(account);
    }
    async capture(func, title) {
        try {
            const result = await func();
            logger('%o', { title, level: 'debug' });
            return result;
        }
        catch (error) {
            logger('%o', { title, level: 'error', error: error.message });
            throw error;
        }
    }
    get walletsObj() {
        return this.walletsInternal$.getValue();
    }
    get currentAccount() {
        return this.currentAccountInternal$.getValue();
    }
}

//# sourceMappingURL=LocalKeyStore.js.map
